import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, Phone, Sparkles, User } from 'lucide-react';

export const smartEscalation: Playbook = {
  meta: {
    id: 'smart-escalation',
    slug: 'smart-escalation',
    categoryId: 'provide-support',
    name: 'The Smart Escalation',
    tagline: 'Escalate to a human, intelligently.',
    description: 'A Genie that knows when to handle issues and when to bring in human experts, with seamless handoffs.',
    strategy: "The Genie attempts to answer the question first. If it can't, it seamlessly collects the user's details and creates a support ticket or initiates a live handoff.",
    benefits: [
      'Reduce false escalations by 60%',
      'Faster resolution times',
      'Better context for human teammates',
      'Improved customer satisfaction'
    ],
    targetAudience: ['Enterprise companies', 'Technical support teams', 'Healthcare providers'],
    estimatedSetupTime: '30 minutes',
    difficulty: 'intermediate',
    tags: ['support', 'escalation', 'handoff', 'triage']
  },
  seo: {
    metaTitle: 'Smart Escalation Playbook - Intelligent Support Handoffs',
    metaDescription: 'Learn how to implement a smart escalation Genie that knows when to handle issues and when to bring in human experts.',
    keywords: ['smart escalation', 'support handoff', 'Genie triage', 'customer support', 'ticket routing']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "I'm here to help! Let me try to answer your question. If I can't fully resolve it, I'll make sure to connect you with the right person who can.",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "Hello! How can I help you today?" },
    { speaker: 'user', message: "I'm having a complex billing issue" },
    { speaker: 'agent', message: "I understand billing issues can be frustrating. Let me connect you with our billing specialist." },
    { speaker: 'user', message: "Yes please" },
    { speaker: 'agent', message: "I've created a priority ticket. Sarah from billing will call you within 30 minutes." }
  ],
  templateConfig: {
    firstMessage: "Hello! I'll do my best to help you. If I can't fully resolve your issue, I'll connect you with a specialist.",
    systemPrompt: "You are a support triage Genie. Try to resolve issues using the knowledge base. If you cannot fully help, gather relevant information and create a ticket or initiate a handoff to a human teammate.",
    tools: ['knowledge_base_search', 'ticket_creation', 'live_chat_handoff', 'email_capture']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer reports complex issue',
      description: 'Technical problem, billing dispute, or product defect requiring specialist attention'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Attempts first-level resolution',
      description: 'Tries common solutions and gathers detailed information about the problem'
    },
    {
      type: 'action',
      icon: Sparkles,
      genieIcon: 'settings',
      label: 'Intelligent issue classification',
      description: 'Categorizes by urgency, department, and required expertise level using smart analysis'
    },
    {
      type: 'action',
      icon: Phone,
      genieIcon: 'share',
      label: 'Creates enriched support ticket',
      description: 'Routes to correct specialist team with full context, screenshots, and conversation history'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'settings',
      label: 'Sets clear expectations',
      description: 'Provides realistic timeline, next steps, and interim solutions or workarounds'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Expert resolution delivered',
      description: 'Specialist contacts customer within promised timeframe with targeted solution'
    }
  ],
  metrics: {
    conversionRate: '92%',
    responseTime: '30 seconds',
    satisfactionScore: '4.9/5',
    costSavings: '$75k/month',
    timeToValue: '2 days'
  }
};